import { isEmpty } from 'radash';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { ValidationProvider } from '@/dialogs/item-editor/Context/ValidationContext';
import { ItemComplianceIndicator } from '@/dialogs/item-editor/item-editor-header/ItemComplianceIndicator';
import { useTranslation } from '@/hooks/useTranslation';

import { buildPriorItemSubmission } from './buildPriorItemSubmission.ts';
import { PriorItemForm } from './component/PriorItemForm';
import PriorItemUpdateAttributeValues from './component/PriorItemUpdateAttributeValues';

interface PriorItemDialogProps {
    dialogTitle: string;
    isCategorySpecific: boolean;
    preselectedCategory?: string;
    getTemplateAttributesMap: (category?: string) => { [key: string]: any };
    onClose: (toWait?: boolean, values?: object) => void;
}

const PriorItemDialog = ({
    dialogTitle,
    isCategorySpecific,
    preselectedCategory,
    getTemplateAttributesMap,
    onClose,
}: PriorItemDialogProps) => {
    const { t } = useTranslation();
    // Default to true so the initial no-category state (before any effect
    // has run) doesn't spuriously enable/disable Submit; when the category
    // effect runs it will drive this to the correct value.
    const [hasResolvableAttributes, setHasResolvableAttributes] = useState(true);

    const onDialogClosed = (values?: object) => {
        onClose(undefined, values);
    };

    return (
        <Dialog open={true} onOpenChange={() => onDialogClosed()}>
            <PriorItemForm defaultValues={isEmpty(preselectedCategory) ? {} : { category__: preselectedCategory }}>
                <ValidationProvider>
                    <DialogContent
                        variant="large"
                        onPointerDownOutside={(e) => e.preventDefault()}
                        onInteractOutside={(e) => e.preventDefault()}
                    >
                        <DialogHeader>
                            <DialogHeaderDefault afterTitle={<ItemComplianceIndicator />}>
                                {t(dialogTitle)}
                            </DialogHeaderDefault>
                        </DialogHeader>
                        <DialogBody padding="none" className="tw-flex tw-flex-col tw-min-h-0 tw-h-[80vh] tw-overflow-hidden tw-p-6">
                            <PriorItemUpdateAttributeValues
                                getTemplateAttributesMap={getTemplateAttributesMap}
                                isCategorySpecific={isCategorySpecific}
                                preselectedCategory={preselectedCategory}
                                onResolvableAttributesChange={setHasResolvableAttributes}
                            />
                        </DialogBody>
                        <DialogFooter>
                            <SubmitButton
                                buttonText={dialogTitle}
                                isCategorySpecific={isCategorySpecific}
                                getTemplateAttributesMap={getTemplateAttributesMap}
                                hasResolvableAttributes={hasResolvableAttributes}
                                onDialogClosed={onDialogClosed}
                            />
                            <DialogClose asChild={true}>
                                <Button
                                    variant="secondary"
                                    onClick={() => onDialogClosed()}
                                >
                                    {t('CLOSE')}
                                </Button>
                            </DialogClose>
                        </DialogFooter>
                    </DialogContent>
                </ValidationProvider>
            </PriorItemForm>
        </Dialog>
    );
};
PriorItemDialog.displayName = 'PriorItemDialog';

const SubmitButton = ({
    buttonText,
    isCategorySpecific,
    getTemplateAttributesMap,
    hasResolvableAttributes,
    onDialogClosed,
}: {
    buttonText: string;
    isCategorySpecific: boolean;
    getTemplateAttributesMap: (category?: string) => { [key: string]: any };
    hasResolvableAttributes: boolean;
    onDialogClosed: (values?: object) => void;
}) => {
    const { t } = useTranslation();
    const { getValues, watch } = useFormContext();
    const currentSelectedCategory = watch('category__');

    const isDisabled = (isCategorySpecific && !currentSelectedCategory)
        || !hasResolvableAttributes;

    return (
        <Button
            disabled={isDisabled}
            onClick={() => {
                // Only the chosen category's template keys are submitted.
                // Switching category in the All / Uncategorized flow must
                // not leak attributes filled for the previous category.
                // Untouched keys are sent as null (LAX-10101).
                onDialogClosed(buildPriorItemSubmission(
                    getValues(),
                    getTemplateAttributesMap(currentSelectedCategory),
                ));
            }}
            data-testid="set-template-values-btn"
        >
            {t(buttonText)}
        </Button>
    );
};
SubmitButton.displayName = 'SubmitButton';

export { PriorItemDialog, type PriorItemDialogProps };
